import React, { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useMunicipalities } from '../hooks/useMunicipalities';
import { MUNICIPALITY_KIND_LABELS } from '../utils/format.utils';
import type { Municipality } from '../types/pre-registration.types';

const ALL_LABEL = 'Todos';

// Case- and accent-insensitive, so "palpala" finds "Palpalá"
const normalize = (text: string) =>
  text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const matchesSearch = (value: string, search: string) =>
  normalize(value).includes(normalize(search)) ? 1 : 0;

interface MunicipalitySelectProps {
  id?: string;
  value?: number;
  onChange: (municipalityId?: number) => void;
  // Filters offer "Todos" (undefined); forms that require a municipality turn it off
  allowAll?: boolean;
  // Shown when nothing is selected and "Todos" is not offered
  placeholder?: string;
  disabled?: boolean;
  // Adds the kind ("Comisión municipal"…) to each name
  showKind?: boolean;
}

// Searchable municipality picker; with `allowAll`, `undefined` means "all municipalities"
export const MunicipalitySelect: React.FC<MunicipalitySelectProps> = ({
  id,
  value,
  onChange,
  allowAll = true,
  placeholder = 'Elegí un municipio',
  disabled,
  showKind = false,
}) => {
  const [open, setOpen] = useState(false);
  const { data: municipalities = [], isLoading } = useMunicipalities();
  const selected = municipalities.find((municipality) => municipality.id === value);
  const label = (municipality: Municipality) =>
    showKind
      ? `${municipality.name} (${MUNICIPALITY_KIND_LABELS[municipality.kind]})`
      : municipality.name;

  const select = (municipalityId?: number) => {
    onChange(municipalityId);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || isLoading}
          className="w-full justify-between font-normal"
        >
          <span className={cn('truncate', !selected && !allowAll && 'text-muted-foreground')}>
            {selected ? label(selected) : allowAll ? ALL_LABEL : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command filter={matchesSearch}>
          <CommandInput placeholder="Buscar municipio..." />
          <CommandList>
            <CommandEmpty>No se encontró el municipio.</CommandEmpty>
            <CommandGroup>
              {allowAll && (
                <CommandItem value={ALL_LABEL} onSelect={() => select(undefined)}>
                  <Check className={cn('mr-2 h-4 w-4', value === undefined ? 'opacity-100' : 'opacity-0')} />
                  {ALL_LABEL}
                </CommandItem>
              )}
              {municipalities.map((municipality) => (
                <CommandItem
                  key={municipality.id}
                  value={municipality.name}
                  onSelect={() => select(municipality.id)}
                >
                  <Check
                    className={cn('mr-2 h-4 w-4', value === municipality.id ? 'opacity-100' : 'opacity-0')}
                  />
                  {label(municipality)}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
